import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Loader2, RefreshCw, RotateCw, XCircle, Inbox, CheckCircle2, AlertTriangle, Clock, Search } from "lucide-react";

const STATUS_META = {
  created: { label: "In coda", cls: "bg-slate-500/15 text-slate-300 border-slate-500/30" },
  sending: { label: "In invio", cls: "bg-blue-500/15 text-blue-300 border-blue-500/30" },
  sent: { label: "Inviata", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  retrying: { label: "In attesa retry", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  failed: { label: "Fallita", cls: "bg-red-500/15 text-red-300 border-red-500/30" },
  cancelled: { label: "Annullata", cls: "bg-zinc-600/20 text-zinc-400 border-zinc-600/30" },
};

const FILTERS = [
  { key: "all", label: "Tutte" },
  { key: "created", label: "In coda" },
  { key: "retrying", label: "Retry" },
  { key: "sent", label: "Inviate" },
  { key: "failed", label: "Fallite" },
  { key: "cancelled", label: "Annullate" },
];

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("it-IT", { timeZone: "Europe/Rome", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

const StatCard = ({ icon: Icon, label, value, accent, sub, testid }) => (
  <Card className={`rounded-lg border-border p-4 flex items-center gap-3 ${accent}`} data-testid={testid}>
    <div className="h-10 w-10 rounded-full bg-background/40 flex items-center justify-center shrink-0">
      <Icon className="h-5 w-5" />
    </div>
    <div className="min-w-0">
      <div className="text-2xl font-heading font-bold leading-none">{value}</div>
      <div className="text-xs text-muted-foreground mt-1">{label}</div>
      {sub && <div className="text-[11px] text-muted-foreground/80 mt-0.5 truncate">{sub}</div>}
    </div>
  </Card>
);

export default function EmailQueue() {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([
        api.get("/admin/email-queue/stats"),
        api.get(`/admin/email-queue?status=${filter}&limit=200`),
      ]);
      setStats(s.data);
      setItems(l.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { setLoading(true); load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  const act = async (id, action) => {
    setBusy(id + action);
    try {
      await api.post(`/admin/email-queue/${id}/${action}`);
      toast.success(action === "retry" ? "Email rimessa in coda" : "Email annullata");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Operazione non riuscita");
    } finally {
      setBusy(null);
    }
  };

  const retryAllFailed = async () => {
    setBusy("retry-failed");
    try {
      const { data } = await api.post("/admin/email-queue/retry-failed");
      toast.success(`${data.requeued} email fallite rimesse in coda`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Operazione non riuscita");
    } finally {
      setBusy(null);
    }
  };

  const c = stats?.counts || {};
  const nextRetry = stats?.upcoming_retries?.[0];

  return (
    <div data-testid="email-queue-page">
      <header className="sticky top-14 lg:top-0 z-10 h-16 border-b border-border bg-background flex items-center justify-between px-4 lg:px-8">
        <div>
          <h1 className="text-xl font-heading font-bold">Coda email</h1>
          <p className="text-xs text-muted-foreground">Monitoraggio invii, retry automatici e log di ogni messaggio</p>
        </div>
        <div className="flex items-center gap-2">
          {(c.failed || 0) > 0 && (
            <Button variant="outline" size="sm" onClick={retryAllFailed} disabled={busy === "retry-failed"} data-testid="retry-all-failed-btn">
              {busy === "retry-failed" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RotateCw className="h-4 w-4 mr-1.5" />} Reinvia falliti
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={load} data-testid="refresh-queue-btn">
            <RefreshCw className="h-4 w-4 mr-1.5" /> Aggiorna
          </Button>
        </div>
      </header>

      <div className="p-4 lg:p-8 space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard icon={Inbox} label="In coda" value={stats?.in_queue ?? "—"} accent="text-slate-200" sub={`${c.created || 0} nuove · ${c.retrying || 0} in retry`} testid="stat-in-queue" />
          <StatCard icon={CheckCircle2} label="Inviate" value={c.sent ?? "—"} accent="text-emerald-300" testid="stat-sent" />
          <StatCard icon={AlertTriangle} label="Fallite" value={c.failed ?? "—"} accent="text-red-300" testid="stat-failed" />
          <StatCard icon={Clock} label="Prossimo retry" value={c.retrying ?? "—"} accent="text-amber-300" sub={nextRetry ? `${nextRetry.to} · ${fmt(nextRetry.next_retry_at)}` : "nessuno programmato"} testid="stat-next-retry" />
        </div>

        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`filter-${f.key}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${filter === f.key ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-secondary"}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <Card className="rounded-lg border-border overflow-x-auto">
          {loading ? (
            <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
          ) : items.length === 0 ? (
            <div className="h-40 flex items-center justify-center text-sm text-muted-foreground">Nessuna email in questo stato</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-border">
                  <TableHead className="text-xs uppercase tracking-wider">Stato</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Destinatario</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Oggetto</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Tentativi</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Prossimo / Ultimo</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Errore</TableHead>
                  <TableHead className="text-right text-xs uppercase tracking-wider">Anteprima / Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((it) => {
                  const meta = STATUS_META[it.status] || STATUS_META.created;
                  const attempts = `${it.attempts || 0}/${it.max_attempts || 5}${it.quota_attempts ? ` · ${it.quota_attempts}×429` : ""}`;
                  const when = it.status === "sent" ? fmt(it.sent_at) : (it.status === "retrying" ? fmt(it.next_retry_at) : fmt(it.updated_at));
                  return (
                    <TableRow key={it.id} className="border-border align-top" data-testid={`queue-row-${it.id}`}>
                      <TableCell><Badge variant="outline" className={`font-normal ${meta.cls}`} data-testid={`queue-status-${it.id}`}>{meta.label}</Badge></TableCell>
                      <TableCell className="text-sm max-w-[180px] truncate">{it.to}{it.is_alert && <span className="ml-1 text-[10px] text-amber-400">⚠︎</span>}</TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[240px] truncate">{it.subject}</TableCell>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{attempts}</TableCell>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{when}</TableCell>
                      <TableCell className="text-xs text-red-300/80 max-w-[220px] truncate" title={it.last_error || ""}>{it.last_error || "—"}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => setPreview(it)} data-testid={`queue-preview-${it.id}`} title="Anteprima email">
                            <Search className="h-3.5 w-3.5" />
                          </Button>
                          {it.status !== "sent" && (
                            <>
                              <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => act(it.id, "retry")} disabled={busy === it.id + "retry"} data-testid={`queue-retry-${it.id}`} title="Reinvia ora">
                                {busy === it.id + "retry" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCw className="h-3.5 w-3.5" />}
                              </Button>
                              {it.status !== "cancelled" && (
                                <Button variant="ghost" size="sm" className="h-7 px-2 text-red-300" onClick={() => act(it.id, "cancel")} disabled={busy === it.id + "cancel"} data-testid={`queue-cancel-${it.id}`} title="Annulla">
                                  {busy === it.id + "cancel" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <XCircle className="h-3.5 w-3.5" />}
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Card>
      </div>

      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) setPreview(null); }}>
        <DialogContent className="max-w-3xl" data-testid="email-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-base pr-6">{preview?.subject || "Anteprima email"}</DialogTitle>
            <DialogDescription className="text-xs">A: {preview?.to || "—"}{preview?.label ? ` · ${preview.label}` : ""}</DialogDescription>
          </DialogHeader>
          <iframe title="anteprima-email" data-testid="email-preview-iframe" srcDoc={preview?.html || "<p style='font-family:sans-serif;padding:16px;color:#666'>Nessun contenuto disponibile per questa email.</p>"} className="w-full h-[62vh] rounded border border-border bg-white" />
        </DialogContent>
      </Dialog>
    </div>
  );
}
